import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { formatMoney } from "@finora/config";
import type { ProductDto } from "@finora/types";
import { api } from "../lib/api";

export interface ProductAutocompleteProps {
  onSelect: (product: ProductDto) => void;
  placeholder?: string;
  /** Ako je prosleđeno, polje TRAJNO prikazuje ovu vrednost (npr. naziv već
   * izabranog artikla) umesto da se resetuje na prazno posle svakog izbora -
   * potrebno kad se autocomplete koristi kao "trenutni izbor" po redu
   * (npr. prijem robe), a ne kao "dodaj u listu pa isprazni" (POS/faktura). */
  value?: string;
}

/**
 * Tekstualno polje koje, dok kucaš, ispod prikazuje listu artikala koji
 * odgovaraju pretrazi (kao "ajax" pretraga) - poziva isti
 * `GET /api/products?search=...` endpoint koji koristi i POS ekran.
 * Pretraga je debounced (250ms) da ne bismo gađali API na svaki taster.
 */
export function ProductAutocomplete({ onSelect, placeholder, value }: ProductAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) setInputValue(value);
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(inputValue), 250);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        if (value !== undefined) setInputValue(value);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const { data: products, isFetching } = useQuery({
    queryKey: ["products", debouncedValue],
    queryFn: () => api.listProducts({ search: debouncedValue || undefined, ingredients: "include" }),
    enabled: isOpen,
  });

  function handleSelect(product: ProductDto) {
    onSelect(product);
    if (value !== undefined) {
      setInputValue(product.name);
    } else {
      setInputValue("");
      setDebouncedValue("");
    }
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="relative block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder ?? "Pretraži artikal ili uslugu..."}
          className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
        />
      </label>

      {isOpen && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border bg-surface shadow-card">
          {isFetching ? (
            <p className="px-3 py-3 text-sm text-slate-400">Pretraga...</p>
          ) : !products || products.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-400">Nema artikala za prikaz.</p>
          ) : (
            <ul>
              {products.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(product)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-brand-light"
                  >
                    <span>
                      <span className="block font-medium text-slate-800">{product.name}</span>
                      <span className="block text-xs text-slate-400">{product.sku}</span>
                    </span>
                    <span className="flex-shrink-0 font-medium text-slate-700">
                      {formatMoney(product.unitPriceGross)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
